import { useState, useEffect, useMemo, useCallback } from 'react';
import { subscribeFacturasEmitidas, deleteFacturaEmitida } from '../../../../../firebase/facturacionUtils';
import { useTableState } from '../../../TablerosGestion/hooks/useTableState'; // Ajusta la ruta relativa a useTableState
import { sanitizeAmount } from '../../helpers';

export const useFacturasEmitidasTable = () => {
    const [facturas, setFacturas] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados para gestión de modales
    const [showModal, setShowModal] = useState(false);
    const [editingFactura, setEditingFactura] = useState(null);
    const [facturaToDelete, setFacturaToDelete] = useState(null);
    const [facturaToView, setFacturaToView] = useState(null);

    // 1. Suscripción en tiempo real a Firestore
    useEffect(() => {
        setLoading(true);
        const unsubscribe = subscribeFacturasEmitidas((data) => {
            setFacturas(data || []);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // 2. Mapeo e Hidratación de datos procesados
    const itemsTable = useMemo(() => {
        return facturas.map(f => {
            const moneda = f.moneda || 'ARS';
            const total = sanitizeAmount(f.montoTotal ?? f.precioFinal);
            const neto = sanitizeAmount(f.montoNeto ?? f.precioNeto);
            const ingresoBanco = sanitizeAmount(f.montoIngresoBanco);
            const tieneRegistroBanco = f.montoIngresoBanco !== undefined && f.montoIngresoBanco !== '';
            
            const retencionesTotales = sanitizeAmount(f.retencionesTotales);
            const cobroEsperado = total - retencionesTotales;
            const diferencia = f.diferenciaCobro !== undefined ? f.diferenciaCobro : (ingresoBanco > 0 ? ingresoBanco - cobroEsperado : 0);

            const esFaltante = tieneRegistroBanco && diferencia < 0;
            const esExcedente = tieneRegistroBanco && diferencia > 0;
            const tieneDesfasaje = esFaltante || esExcedente;

            return {
                ...f,
                moneda,
                montoTotal: total,
                montoNeto: neto,
                montoIngresoBanco: ingresoBanco,
                tieneRegistroBanco,
                diferencia,
                esFaltante,
                esExcedente,
                tieneDesfasaje,
                clienteNombreDisplay: (f.clienteNombre || 'Sin Cliente').toUpperCase(),
                conceptoDisplay: (f.conceptoNombre || f.conceptoIngreso || 'Sin concepto').toUpperCase(),
                areaNombreDisplay: f.areaNombre || 'S/A',
                periodoDisplay: f.periodo || '-'
            };
        });
    }, [facturas]);

    // 3. Instancia de useTableState (Motor del Tablero de Gestión)
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaEmision', direction: 'descending' },
        initialColumns: {
            fechaEmision: true,
            numeroFactura: true,
            clienteNombreDisplay: true,
            conceptoDisplay: true,
            areaNombreDisplay: true,
            montoNeto: true,
            montoTotal: true,
            montoIngresoBanco: true,
            estatus: true
        },
        initialFilters: {},
        recordsPerPage: 100
    });

    // Handlers para modales
    const handleOpenCreate = useCallback(() => {
        setEditingFactura(null);
        setShowModal(true);
    }, []);

    const handleOpenEdit = useCallback((factura) => {
        setEditingFactura(factura);
        setShowModal(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!facturaToDelete) return;
        try {
            await deleteFacturaEmitida(facturaToDelete.id);
            setFacturaToDelete(null);
        } catch (error) {
            console.error("Error al eliminar factura emitida:", error);
        }
    }, [facturaToDelete]);

    return {
        state: {
            loading,
            showModal,
            editingFactura,
            facturaToDelete,
            facturaToView,
            itemsTable
        },
        actions: {
            setShowModal,
            setEditingFactura,
            setFacturaToDelete,
            setFacturaToView,
            handleOpenCreate,
            handleOpenEdit,
            handleDelete
        },
        tableState
    };
};